import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { productsAPI } from '../../services/productsAPI'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import Select from '../UI/Select'
import { X, Package, Upload } from 'lucide-react'
import toast from 'react-hot-toast'

export default function CreateProductModal({ isOpen, onClose }) {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm({
    defaultValues: {
      name: '',
      description: '',
      status: 'active',
      url: '',
      category: ''
    }
  })

  const mutation = useMutation((productData) => productsAPI.create(productData), {
    onSuccess: () => {
      queryClient.invalidateQueries(['products'])
      toast.success('Product created successfully!')
      reset()
      onClose()
    },
    onError: (error) => {
      console.error('Error creating product:', error)
      toast.error(error.response?.data?.message || 'Failed to create product')
    }
  })

  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file')
        return
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB')
        return
      }
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
    setValue('image', null)
  }

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('description', data.description || '')
      formData.append('status', data.status)
      formData.append('url', data.url || '')
      formData.append('category', data.category || '')
      
      if (selectedImage) {
        formData.append('image', selectedImage)
      }

      await mutation.mutateAsync(formData)
    } catch (error) {
      // Error handled in mutation onError
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      setImagePreview(null)
      setSelectedImage(null)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <Package className="h-4 w-4 text-primary-600" />
            </div>
            <CardTitle>Create New Product</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Product Name *
              </label>
              <input
                {...register('name', { 
                  required: 'Product name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' }
                })}
                type="text"
                className="input w-full"
                placeholder="Enter product name"
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="input w-full resize-none"
                placeholder="Enter product description"
                disabled={isSubmitting}
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Status
              </label>
              <select
                {...register('status')}
                className="input w-full"
                disabled={isSubmitting}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Product Image
              </label>
              {imagePreview ? (
                <div className="relative">
                  <div className="w-full h-48 rounded-lg border border-secondary-200 overflow-hidden bg-secondary-50 flex items-center justify-center">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={removeImage}
                    className="mt-2"
                    disabled={isSubmitting}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Remove Image
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="image-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-secondary-300 border-dashed rounded-lg cursor-pointer bg-secondary-50 hover:bg-secondary-100 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-secondary-400" />
                      <p className="mb-2 text-sm text-secondary-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-secondary-400">PNG, JPG, GIF up to 5MB</p>
                    </div>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                      disabled={isSubmitting}
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Category
              </label>
              <Select
                {...register('category')}
                className="input w-full"
                disabled={isSubmitting}
              >
                <option value="">Select a category</option>
                <option value="Banking & Financial">Banking & Financial</option>
                <option value="Technology">Technology</option>
                <option value="ERP">ERP</option>
                <option value="ITSM">ITSM</option>
                <option value="Insurance">Insurance</option>
                <option value="Automotive">Automotive</option>
                <option value="Sales & Marketing">Sales & Marketing</option>
                <option value="Lifestyle">Lifestyle</option>
                <option value="Other">Other</option>
              </Select>
            </div>

            {/* URL */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                URL
              </label>
              <input
                {...register('url', {
                  pattern: {
                    value: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
                    message: 'Please enter a valid URL'
                  }
                })}
                type="url"
                className="input w-full"
                placeholder="https://example.com"
                disabled={isSubmitting}
              />
              {errors.url && (
                <p className="mt-1 text-sm text-red-600">{errors.url.message}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[100px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Create Product'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}



